import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Store, Leaf, ExternalLink } from 'lucide-react';
import farmRecommendationAgent from '../../services/agents/farmRecommendationAgent';
import linkingHelper from '../../utils/linkingHelper';

interface Farm {
  id: string;
  name: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  categories: string[];
  products: string[];
  rating?: number;
  distance?: number;
  certifications?: string[];
  specialty?: string[];
}

interface FarmSourcesWidgetProps {
  ingredients: string[];
  maxFarms?: number;
  title?: string;
  showDistance?: boolean;
}

const FarmSourcesWidget: React.FC<FarmSourcesWidgetProps> = ({
  ingredients,
  maxFarms = 5,
  title = 'Where to Buy Ingredients',
  showDistance = true,
}) => {
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFarms = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!ingredients || ingredients.length === 0) {
          setFarms([]);
          setLoading(false);
          return;
        }

        // Get farms that sell any of the ingredients
        const foundFarms = farmRecommendationAgent.getFarmsByProducts(
          ingredients,
          false, // matchAll = false (any ingredient)
          maxFarms
        );

        setFarms(foundFarms);
      } catch (err) {
        console.error('Error fetching farm sources:', err);
        setError('Unable to load farm sources');
      } finally {
        setLoading(false);
      }
    };

    fetchFarms();
  }, [ingredients, maxFarms]);

  const styles = {
    widget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '2px solid #2D5A27',
    },
    headerIcon: {
      color: '#2D5A27',
      marginRight: '12px',
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0,
    },
    farmCard: {
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
      padding: '16px',
      marginBottom: '12px',
      border: '1px solid #e5e5e5',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    } as React.CSSProperties,
    farmCardHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    farmName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
    },
    farmDetails: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '6px',
    },
    farmDetail: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      color: '#666',
    },
    detailIcon: {
      width: '16px',
      height: '16px',
      marginRight: '8px',
      color: '#2D5A27',
    },
    products: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '6px',
      marginTop: '8px',
    },
    productTag: {
      backgroundColor: '#2D5A27',
      color: '#fff',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
    },
    loading: {
      textAlign: 'center' as const,
      padding: '32px 0',
      color: '#666',
    },
    empty: {
      textAlign: 'center' as const,
      padding: '24px',
      color: '#666',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
    },
    emptyIcon: {
      width: '48px',
      height: '48px',
      margin: '0 auto 12px',
      color: '#ccc',
    },
    viewAll: {
      display: 'inline-flex',
      alignItems: 'center',
      marginTop: '16px',
      color: '#2D5A27',
      fontWeight: '600',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.3s ease',
    },
    viewAllIcon: {
      width: '16px',
      height: '16px',
      marginLeft: '6px',
    },
  };

  if (loading) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <Store style={styles.headerIcon} size={24} />
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.loading}>
          <div className="spinner-border text-success" role="status">
            <span className="sr-only">Loading farms...</span>
          </div>
          <p style={{ marginTop: '12px' }}>Finding local sources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <Store style={styles.headerIcon} size={24} />
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.empty}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (farms.length === 0) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <Store style={styles.headerIcon} size={24} />
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.empty}>
          <Store style={styles.emptyIcon} />
          <p>
            No farms found selling these ingredients yet.
            <br />
            Check back soon or browse our directory.
          </p>
          <Link to={linkingHelper.getSearchUrl('')} style={styles.viewAll}>
            Browse All Farms
            <ExternalLink style={styles.viewAllIcon} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <Store style={styles.headerIcon} size={24} />
        <h3 style={styles.title}>{title}</h3>
      </div>

      {farms.map((farm) => {
        // Match ingredients to farm products
        const matchedProducts = farm.products.filter((product) =>
          ingredients.some((ing) =>
            product.toLowerCase().includes(ing.toLowerCase()) ||
            ing.toLowerCase().includes(product.toLowerCase())
          )
        );

        return (
          <Link
            key={farm.id}
            to={linkingHelper.getFarmUrl(farm.id)}
            style={{ textDecoration: 'none' }}
          >
            <div
              style={styles.farmCard}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, styles.farmCardHover);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={styles.farmName}>
                <Store size={18} style={{ marginRight: '8px', color: '#2D5A27' }} />
                {farm.name}
              </div>

              <div style={styles.farmDetails}>
                {farm.location && (
                  <div style={styles.farmDetail}>
                    <MapPin style={styles.detailIcon} />
                    {farm.location.address}
                    {showDistance && farm.distance && (
                      <span style={{ marginLeft: '8px', fontWeight: '600' }}>
                        ({farm.distance.toFixed(1)} mi)
                      </span>
                    )}
                  </div>
                )}

                {farm.certifications && farm.certifications.length > 0 && (
                  <div style={styles.farmDetail}>
                    <Leaf style={styles.detailIcon} />
                    {farm.certifications.includes('organic') ||
                    farm.certifications.includes('USDA Organic')
                      ? 'Certified Organic'
                      : farm.certifications[0]}
                  </div>
                )}
              </div>

              {matchedProducts.length > 0 && (
                <div style={styles.products}>
                  {matchedProducts.slice(0, 3).map((product, idx) => (
                    <span key={idx} style={styles.productTag}>
                      {product}
                    </span>
                  ))}
                  {matchedProducts.length > 3 && (
                    <span style={styles.productTag}>
                      +{matchedProducts.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </Link>
        );
      })}

      {farms.length >= maxFarms && (
        <Link
          to={linkingHelper.getSearchUrl('')}
          style={styles.viewAll}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#1a3d1a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#2D5A27';
          }}
        >
          View All Farms
          <ExternalLink style={styles.viewAllIcon} />
        </Link>
      )}
    </div>
  );
};

export default FarmSourcesWidget;
